import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import authApi from '../api/auth/authApi';
import { AUTH_EXPIRED_EVENT } from '../api/client';
import storageService from '../services/storageService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => storageService.getSession()?.user || null);

  const signIn = useCallback((session, remember = false) => {
    storageService.setSession(session, remember);
    setUser(session.user);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (e) {
      /* ignore — clear locally regardless */
    }
    storageService.clearSession();
    setUser(null);
  }, []);

  const updateUser = useCallback((next) => {
    setUser(next);
    storageService.updateSession({ user: next });
  }, []);

  useEffect(() => {
    const onExpired = () => {
      storageService.clearSession();
      setUser(null);
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  const value = useMemo(
    () => ({ user, isAuthenticated: !!user, signIn, signOut, updateUser }),
    [user, signIn, signOut, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
